import { Alert, Box, Button, CircularProgress, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { PayPalButtons, PayPalScriptProvider, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { gateway } from "../gateway";
import { initialBenefitsFetch } from "../permissions/benefits/redux/benefits.init";
import { getEnvVar } from "../utils/get-env";
import { EQueryKeys } from "./subscriptions/hooks/useMySubscriptionInfo.query";

export interface PlanPaymentPayAsYouGoProps {
  plan?: any;
  setSubscriptionState?: (stateObj: { state: "none" | "success" | "error"; data?: any }) => void;
}

const stripePublishableKey = getEnvVar("REACT_APP_STRIPE_PUBLISHABLE_KEY") ?? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "";
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const PayPalVaultButtonsContainer: React.FC<any> = (props) => {
  const [{ isPending }] = usePayPalScriptReducer();

  if (isPending) {
    return (
      <Box sx={{ width: "100%", py: 1 }}>
        <Stack spacing={1.5}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={48}
            sx={{
              borderRadius: 2,
              bgcolor: "rgba(251, 142, 0, 0.12)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={48}
            sx={{
              borderRadius: 2,
              bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
            }}
          />
        </Stack>
      </Box>
    );
  }

  return <PayPalButtons {...props} />;
};

const StripeCardFormContainer: React.FC<{
  companyId: any;
  currentPlanId: any;
  phoneNumber: string;
  tokenLocal: string | null;
  setErrorMessage: (msg: string) => void;
  setSubscriptionState: (stateObj: { state: "none" | "success" | "error"; data?: any }) => void;
  queryClient: any;
  dispatch: any;
}> = ({ companyId, currentPlanId, phoneNumber, tokenLocal, setErrorMessage, setSubscriptionState, queryClient, dispatch }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleSubmitCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setErrorMessage("Stripe is not initialized yet. Please try again.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (result.error) {
        setErrorMessage(result.error.message || "Failed to authorize payment method.");
        setSubmitting(false);
        return;
      }

      const setupIntent = result.setupIntent;
      if (!setupIntent || setupIntent.status !== "succeeded") {
        setErrorMessage("Setup Intent did not succeed. Please verify your card details.");
        setSubmitting(false);
        return;
      }

      const paymentMethodId = typeof setupIntent.payment_method === "string" ? setupIntent.payment_method : setupIntent.payment_method?.id;

      if (!paymentMethodId) {
        setErrorMessage("No valid payment method ID returned from Stripe.");
        setSubmitting(false);
        return;
      }

      console.log("Stripe SetupIntent succeeded, exchanging paymentMethodId:", paymentMethodId);

      const response = await gateway.post(
        "/stripe/vault/payment-tokens",
        {
          paymentMethodId,
          companyId: companyId ? Number(companyId) : undefined,
          planId: currentPlanId,
          meta: {
            phoneNumber: phoneNumber || undefined,
          },
        },
        {
          headers: {
            Authorization: tokenLocal ? `Bearer ${tokenLocal}` : undefined,
          },
        },
      );

      const resData = response?.data?.data || response?.data || {};

      setSubscriptionState({
        state: "success",
        data: {
          message: "Pay-As-You-Go subscription activated successfully!",
          subscriptionId: resData?.subscription?.id || resData?.id || paymentMethodId,
          orderId: paymentMethodId,
          subscription: resData?.subscription || null,
          benefits: resData?.benefits || [],
        },
      });

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO],
      });
      dispatch(initialBenefitsFetch() as any);
    } catch (err: any) {
      console.error("Error in Stripe vault payment-tokens exchange:", err);
      const msg = err?.response?.data?.message || err?.message || "Failed to complete payment enrollment with Stripe.";
      setErrorMessage(msg);

      setSubscriptionState({
        state: "error",
        data: {
          message: `Failed to complete Pay-As-You-Go enrollment: ${msg}`,
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmitCard} style={{ width: "100%" }}>
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
          Credit or debit card
        </Typography>

        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            width: "100%",
          }}
        >
          <Stack spacing={2}>
            <PaymentElement
              options={{
                layout: "tabs",
                defaultValues: {
                  billingDetails: {
                    address: {
                      country: "US",
                    },
                  },
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={!stripe || !elements || submitting}
              sx={{ py: 1.2, fontWeight: 700, borderRadius: 2 }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : "Authorize & Save Card"}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </form>
  );
};

const StripeVaultSection: React.FC<{
  companyId: any;
  currentPlanId: any;
  phoneNumber: string;
  tokenLocal: string | null;
  setErrorMessage: (msg: string) => void;
  setSubscriptionState: (stateObj: { state: "none" | "success" | "error"; data?: any }) => void;
  queryClient: any;
  dispatch: any;
}> = (props) => {
  const [clientSecret, setClientSecret] = useState<string>("");
  const [loadingSetup, setLoadingSetup] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    const initSetupIntent = async () => {
      if (!props.companyId) {
        if (isMounted) setLoadingSetup(false);
        return;
      }

      try {
        if (isMounted) {
          setLoadingSetup(true);
          props.setErrorMessage("");
        }

        const response = await gateway.post("/stripe/vault/setup-intent", {
          companyId: Number(props.companyId),
        });

        const resData = response?.data?.data || response?.data || {};
        const secret = resData?.clientSecret || resData?.client_secret;

        if (!secret) {
          throw new Error("No client secret returned from Stripe SetupIntent endpoint.");
        }

        if (isMounted) {
          setClientSecret(secret);
        }
      } catch (err: any) {
        console.error("Error creating Stripe Vault SetupIntent:", err);
        if (isMounted) {
          props.setErrorMessage(err?.response?.data?.message || err?.message || "Failed to initialize Stripe card checkout.");
        }
      } finally {
        if (isMounted) {
          setLoadingSetup(false);
        }
      }
    };

    initSetupIntent();

    return () => {
      isMounted = false;
    };
  }, [props.companyId]);

  if (!stripePromise) {
    return (
      <Alert severity="warning" sx={{ width: "100%" }}>
        Stripe publishable key is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in environment.
      </Alert>
    );
  }

  if (loadingSetup) {
    return (
      <Box sx={{ width: "100%", py: 1 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={120}
          sx={{
            borderRadius: 2,
            bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
          }}
        />
      </Box>
    );
  }

  if (!clientSecret) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
      <StripeCardFormContainer {...props} />
    </Elements>
  );
};

export const PlanPaymentPayAsYouGo: React.FC<PlanPaymentPayAsYouGoProps> = ({ plan, setSubscriptionState = () => {} }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showBoundary } = useErrorBoundary();

  const planState = useSelector((state: any) => state?.planState);
  const planSelected = plan || planState?.planSelected || null;
  const currentPlanId = planSelected?.id || sessionStorage.getItem("payg_plan_id");

  const { activeCompanyId, company, phoneNumber: userPhone, phone: altPhone } = useSelector((state: any) => state?.userState || {});

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const paypalClientId = getEnvVar("REACT_APP_PAYPAL_MERCHANT_CLIENT_ID") || "";
  const paypalPartnerAttributionId = getEnvVar("REACT_APP_PAYPAL_PARTNER_ATTRIBUTION_ID") || "";
  const companyId = activeCompanyId || company?.id;
  const phoneNumber = userPhone || altPhone || company?.phone || "";
  const tokenLocal = sessionStorage.getItem("token");

  const handleExchangeAndEnrollment = useCallback(
    async (tokenToExchange: string) => {
      if (!tokenToExchange) {
        setErrorMessage("Missing setup token ID for payment authorization exchange.");
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        console.log("paypal method: executing /paypal/vault/payment-tokens with token:", tokenToExchange);
        const response = await gateway.post(
          "/paypal/vault/payment-tokens",
          {
            setupTokenId: tokenToExchange,
            companyId: companyId ? String(companyId) : undefined,
            phoneNumber: phoneNumber || undefined,
            planId: currentPlanId,
          },
          {
            headers: {
              Authorization: tokenLocal ? `Bearer ${tokenLocal}` : undefined,
            },
          },
        );

        const resData = response?.data?.data || response?.data || {};

        setSubscriptionState({
          state: "success",
          data: {
            message: "Pay-As-You-Go subscription activated successfully!",
            subscriptionId: resData?.subscriptionId || resData?.id || tokenToExchange,
            orderId: resData?.orderId || resData?.paymentTokenId || tokenToExchange,
            subscription: resData?.subscription || null,
            benefits: resData?.benefits || [],
          },
        });

        await queryClient.invalidateQueries({
          queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO],
        });
        dispatch(initialBenefitsFetch() as any);
      } catch (error: any) {
        console.error("Error in payment-tokens:", error);
        const msg = error?.response?.data?.message || error?.message || "Failed to complete payment enrollment.";
        setErrorMessage(msg);

        setSubscriptionState({
          state: "error",
          data: {
            message: `Failed to complete Pay-As-You-Go enrollment: ${msg}`,
          },
        });
      } finally {
        setLoading(false);
      }
    },
    [companyId, phoneNumber, currentPlanId, tokenLocal, setSubscriptionState, queryClient, dispatch],
  );

  // Check URL params if redirected back to main window with approval_token_id
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl =
      urlParams.get("approval_token_id") ||
      urlParams.get("setupTokenId") ||
      urlParams.get("approval_token") ||
      urlParams.get("token") ||
      urlParams.get("approval_session_id");

    if (tokenFromUrl) {
      console.log("Auto-detecting PayPal token from URL search params:", tokenFromUrl);
      window.history.replaceState({}, document.title, window.location.pathname);
      handleExchangeAndEnrollment(tokenFromUrl);
    }
  }, [handleExchangeAndEnrollment]);

  // Listen for postMessage from popup window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "PAYPAL_VAULT_APPROVED" || event.data?.setupTokenId || event.data?.approval_token || event.data?.approval_token_id) {
        const approvedToken = event.data?.token || event.data?.setupTokenId || event.data?.approval_token || event.data?.approval_token_id;

        console.log("PAYPAL_VAULT_APPROVED received token via postMessage:", approvedToken);
        handleExchangeAndEnrollment(approvedToken);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleExchangeAndEnrollment]);

  if (!paypalClientId) {
    showBoundary(new Error("PayPal client ID is not set"));
    return null;
  }

  const initialOptions: any = {
    "client-id": paypalClientId,
    components: "buttons",
    "data-sdk-integration-source": "integrationbuilder_sc",
    "data-partner-attribution-id": paypalPartnerAttributionId,
    "data-page-type": "checkout",
    vault: "true",
  };

  return (
    <div className="PlanPaymentPayAsYouGo">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          width: "100%",
          py: 2,
        }}
      >
        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 700 }}>
          Flow Mobile Pay-As-You-Go
        </Typography>

        <Typography variant="body2" align="center" color="text.secondary">
          Authorize your payment method to enable automatic usage billing.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ width: "100%" }}>
            {errorMessage}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
            <CircularProgress size={28} />
            <Typography variant="body2">Processing Pay-As-You-Go enrollment...</Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Stack spacing={2} sx={{ width: "100%" }}>
              <StripeVaultSection
                key={`stripe-${currentPlanId}-${companyId}`}
                companyId={companyId}
                currentPlanId={currentPlanId}
                phoneNumber={phoneNumber}
                tokenLocal={tokenLocal}
                setErrorMessage={setErrorMessage}
                setSubscriptionState={setSubscriptionState}
                queryClient={queryClient}
                dispatch={dispatch}
              />

              <Divider sx={{ width: "100%", my: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                  OR
                </Typography>
              </Divider>

              <Stack spacing={1} sx={{ width: "100%" }}>
                <PayPalScriptProvider options={initialOptions}>
                  <PayPalVaultButtonsContainer
                    key={currentPlanId}
                    style={{
                      shape: "rect",
                      layout: "vertical",
                      label: "paypal",
                      color: "gold",
                    }}
                    createVaultSetupToken={async () => {
                      try {
                        setErrorMessage("");
                        if (currentPlanId) {
                          sessionStorage.setItem("payg_plan_id", String(currentPlanId));
                        }
                        const response = await gateway.post("/paypal/vault/setup-tokens", {
                          planId: currentPlanId,
                          paymentSource: "paypal",
                        });
                        const responseData = response?.data?.data || response?.data || {};
                        const tokenId = responseData?.setupTokenId || responseData?.id || responseData?.token;
                        if (!tokenId) {
                          throw new Error(responseData?.message || "No setup token returned from backend");
                        }
                        return tokenId;
                      } catch (err: any) {
                        console.error("Error in createVaultSetupToken (paypal):", err);
                        setErrorMessage(err?.response?.data?.message || err?.message || "Could not initiate PayPal authorization.");
                        throw err;
                      }
                    }}
                    onApprove={async (data: any) => {
                      console.log("PayPal Vault onApprove data:", data);
                      const tokenId = data?.vaultSetupToken || data?.setupTokenId || data?.approval_token_id;
                      if (tokenId) {
                        await handleExchangeAndEnrollment(tokenId);
                      } else {
                        setErrorMessage("PayPal did not return a valid setup token ID.");
                      }
                    }}
                    onCancel={() => {
                      setLoading(false);
                      setErrorMessage("");
                    }}
                    onError={(err: any) => {
                      console.error("PayPal Vault error:", err);
                      setLoading(false);
                      const msg = err?.message || "";
                      if (msg.includes("postrobot_method") || msg.includes("closed")) {
                        setErrorMessage("");
                      } else {
                        setErrorMessage(msg || "PayPal authorization failed.");
                      }
                    }}
                  />
                </PayPalScriptProvider>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    </div>
  );
};
